import { IconButton, Skeleton, Text, Tooltip } from '@radix-ui/themes';
import { CircleDashed, Clock, Mail, Phone, Share } from 'lucide-react';
import type { Ref } from 'react';
import { use, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '../../utils/relativeTime';
import AiDetectionIcon from '../AiDetectionIcon';
import { getScore } from '../common';
import BadgeDropdown from '../elements/BadgeDropdown';
import CandidateDropdown from '../elements/CandidateDropdown';
import DownloadCv from '../elements/DownloadCv';
import ReInvite from '../elements/ReInvite';
import LocaleContext from '../hooks/LocaleContext';
import OpenApplicationDialog from './ApplicationHeader/OpenApplicationDialog';

function isAiDetectedApplication(summaries?: { aiDetection: { isAi: boolean } }[]) {
  if (summaries && Array.isArray(summaries)) {
    return summaries.some((x) => x && x.aiDetection && x.aiDetection.isAi);
  }
  return false;
}

export type Summaries = {
  id: string;
  aiDetection: { isAi: boolean };
  summaryPart: Summary[];
  keywordSummary: { isEmpty: boolean; text: string };
};

export type Summary = {
  header: string;
  points: number;
  maxPoints: number;
  threshold: number;
  details: SummaryDetail[];
};

export type SummaryDetail = {
  icon: string;
  average: number;
  points: number;
  bonuspoints: number;
  question: string;
  answer: string;
  label: string;
  options: {
    label: string;
    answered: boolean;
    isCorrect: boolean;
    evaluation: {
      name: string;
      bonus_points: number;
    };
  }[];
  aiDetection: {
    isAi: boolean;
  };
  reset?: boolean;
  /** Audio/video recording answer: durable playback link, exchanged via the recordingMediaUrl query. */
  media?: {
    kind: 'audio' | 'video';
    link: string;
    durationSeconds?: number;
    /** Video answers: durable link for the snapshot thumbnail (preview/poster). */
    thumbnailLink?: string | null;
  } | null;
};

export type Activity = {
  createdAt: string;
  actionType: string;
  actionSubType: string;
  message: string;
  status: string;
  createdBy: string;
};

export type Application = {
  id: string;
  stage: number;
  status: string;
  score: number;
  createdAt: string;
  activityLog: Activity[];
  candidate: { id: string; firstName: string; lastName: string; email: string; phoneNumber: string };
  job: { id: string; threshold: number; title: string; integration: { teamtailor: { companyId: string } } };
  documents: { id: string }[];
  _summaries: Summaries[];
  integration: { teamtailor: { jobId: string; candidateId: string } };
  interviews: { nodes: { id: string }[] };
  summaries: {
    totalCount: number;
    nodes: Summaries[];
  };
  openApplicationSummary: {
    expiry: number;
  };
  notes: {
    createdAt: Date;
    createdBy: string;
    text: string;
  }[];
  aiDetection: {
    isAi: boolean;
  };
  accepted: boolean;
};

const Header = ({
  application,
  onStatusChange,
  deleteApplication,
  editableStatus = true,
  reInvite = true,
  candidateDropdown = true,
  ref,
}: {
  application?: Application;
  onStatusChange: (value: { type: string; status: string }) => void;
  deleteApplication?: {
    deleteApplicationCallback: () => Promise<void>;
    loading: boolean;
  };
  editableStatus?: boolean;
  reInvite?: boolean;
  candidateDropdown?: boolean;
  ref?: Ref<HTMLDivElement>;
}) => {
  const { t } = useTranslation();
  const { userSettings } = use(LocaleContext);
  const [openApplicationShow, setOpenApplicationShow] = useState(false);

  const closeOpenApplication = () => setOpenApplicationShow(false);

  if (application) {
    let appliedDate, latestActivityDate;
    if (application) appliedDate = new Date(application.createdAt);
    if (application && application.activityLog && application.activityLog.length > 0)
      latestActivityDate = new Date(application.activityLog[application.activityLog.length - 1].createdAt);

    const candidate = application && application.candidate;
    let candidateName = '';

    if (candidate.firstName && candidate.lastName) {
      candidateName = `${candidate.firstName} ${candidate.lastName}`;
    } else if (candidate.firstName) {
      candidateName = candidate.firstName;
    } else if (candidate.lastName) {
      candidateName = candidate.lastName;
    }

    return (
      <div
        className="flex swiper-header mx-0 pl-4"
        ref={ref}
        id="swiper-header"
        data-ph-capture-attribute-region="header"
      >
        <div className="score">
          <div className="my-auto">
            {application && getScore({ application: application, threshold: application.job.threshold, size: 'xl' })}
          </div>
        </div>
        <div className="flex w-full pr-4 my-auto m-0 items-center justify-between">
          <div>
            {application && application.candidate && !userSettings.settings?.anonymized && (
              <span className="capitalize">
                <h1 className="truncate">{candidateName}</h1>
              </span>
            )}

            <div className="swiper-contact flex flex-row">
              <div>
                {application && application.candidate && application.candidate.email && (
                  <div className="flex flex-row items-center">
                    <Mail className="mr-1" size={14} />
                    <span className="mr-2">{application.candidate.email}</span>
                  </div>
                )}
              </div>

              <div>
                {application && application.candidate && application.candidate.phoneNumber && (
                  <div className="flex flex-row items-center">
                    <Phone className="mr-1" size={14} />
                    <span>{application.candidate.phoneNumber}</span>
                  </div>
                )}
              </div>
            </div>

            {(!application.candidate || userSettings.settings?.anonymized) && <h1>{t('swiper.header.applicant')}</h1>}
            {application.candidate && (
              <div className="flex flex-row">
                <BadgeDropdown
                  initStatus={application.status}
                  onChange={onStatusChange}
                  className="clickable header-dropdown"
                  editableStatus={editableStatus}
                />
                <Tooltip
                  content={
                    <Text size="3">
                      {t('swiper.header.appliedDate', { date: appliedDate?.toLocaleString('sv-se') })}
                    </Text>
                  }
                >
                  <div className="time ml-4 flex items-center">
                    {application && <Clock className="mr-1" size={14} />}
                    {appliedDate ? formatRelativeTime(appliedDate) : ''}
                  </div>
                </Tooltip>
                <Tooltip
                  content={
                    <Text size="3">
                      {t('swiper.header.latestActivity', { date: latestActivityDate?.toLocaleString('sv-se') })}
                    </Text>
                  }
                >
                  <div className="time ml-4 flex items-center">
                    <CircleDashed className="mr-1" size={14} />
                    <span className="align-middle">
                      {latestActivityDate ? formatRelativeTime(latestActivityDate) : ''}
                    </span>
                  </div>
                </Tooltip>
              </div>
            )}
          </div>

          <div className="my-auto m-0 flex flex-row gap-1 items-center align-middle justify-between">
            {isAiDetectedApplication(application._summaries ?? application.summaries.nodes) ? (
              <AiDetectionIcon iconSize={32} tooltipText={t('swiper.header.aiTooltip')} />
            ) : null}

            {reInvite === true && (application.stage === null || application.stage < 2) && (
              <ReInvite application={application} />
            )}

            {application && application.openApplicationSummary && (
              <div className="flex flex-row justify-between items-center align-middle gap-1">
                <Tooltip content={<Text size="3">{t('swiper.header.share')}</Text>}>
                  <IconButton
                    color="gray"
                    variant="soft"
                    size="3"
                    className="ml-2"
                    onClick={() => setOpenApplicationShow(!openApplicationShow)}
                  >
                    <Share size={20} />
                  </IconButton>
                </Tooltip>

                <OpenApplicationDialog
                  onHide={() => closeOpenApplication()}
                  show={openApplicationShow}
                  openApplication={application.openApplicationSummary}
                  applicationId={application.id}
                />
              </div>
            )}

            {application.documents &&
              application.documents.map((doc) => (
                <DownloadCv key={doc.id} applicationId={application.id} fileId={doc.id} className="ml-2" />
              ))}
            {candidateDropdown && (
              <CandidateDropdown application={application} deleteApplication={deleteApplication}></CandidateDropdown>
            )}
          </div>
        </div>
      </div>
    );
  } else return <SkeletonHeader />;
};

export default Header;

const SkeletonHeader = () => {
  return (
    <div className="flex swiper-header mx-0 pl-4">
      <div className="score">
        <span className="my-auto">
          <Skeleton width="3rem" height="3rem" style={{ borderRadius: '50%' }} />
        </span>
      </div>
      <div className="w-full px-4 ">
        <span className="capitalize user-select-none">
          <h1 className="truncate py-1">
            <Skeleton width="8rem" height="2rem" />
          </h1>
          <Skeleton className="swiper-contact" width="8rem" />
        </span>
        <div className="flex py-1">
          <span>
            <Skeleton width="5rem" height="0.9rem" />
          </span>
          <span className="time ml-4 flex items-center">
            <Skeleton width="1rem" height="1rem" style={{ borderRadius: '50%' }} />
            <span className="mx-1" />
            <Skeleton width="2rem" />
          </span>
          <span className="time ml-4 flex items-center">
            <Skeleton width="1rem" height="1rem" style={{ borderRadius: '50%' }} />
            <span className="mx-1" />
            <Skeleton width="2rem" />
          </span>
        </div>
      </div>
    </div>
  );
};
